import React, { useEffect, useState, useRef, createElement } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { Scale, Clock, Search, Lock, Shield, CheckCircle, Award, FileText, Database, Users, Zap, ArrowRight, Mail, Building2, Activity } from 'lucide-react';
// Animate a number from 0 to end when triggered
function animateValue(end: number, duration: number, onUpdate: (val: number) => void) {
  let startTime: number | null = null;
  let frame: number;
  const animate = (timestamp: number) => {
    if (!startTime) startTime = timestamp;
    const progress = timestamp - startTime;
    const pct = Math.min(progress / duration, 1);
    const easeOut = pct === 1 ? 1 : 1 - Math.pow(2, -10 * pct);
    onUpdate(Math.floor(end * easeOut));
    if (pct < 1) frame = requestAnimationFrame(animate);
  };
  frame = requestAnimationFrame(animate);
  return () => cancelAnimationFrame(frame);
}
export function AboutPage() {
  useEffect(() => {
    document.title = 'About Us | UK Medicolegal Assessment';
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', 'Independent medico-legal agency managing a panel of 1,000+ GMC-registered specialists. Clinical governance, fast turnaround, and court-ready reports for solicitors and insurers.');
    setMeta('robots', 'index, follow');
  }, []);
  const statsRef = useRef<HTMLDivElement>(null);
  const [stats15k, setStats15k] = useState(0);
  const [stats1k, setStats1k] = useState(0);
  const [stats98, setStats98] = useState(0);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        animateValue(15000, 2000, setStats15k);
        animateValue(1000, 2000, setStats1k);
        animateValue(98, 2000, setStats98);
        observer.disconnect();
      }
    }, {
      threshold: 0.1
    });
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);
  return <div className="min-h-screen bg-white" data-id="element-483">
      <Navigation data-id="element-484" />

      {/* 1. HERO SECTION */}
      <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 bg-[#1A2B3C] overflow-hidden" data-id="element-485">
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1A2B3C] via-[#1A2B3C] to-[#2D3748] opacity-90" data-id="element-486"></div>
        <div className="absolute top-0 right-0 -translate-y-12 translate-x-1/3 w-[800px] h-[800px] bg-teal-500/10 rounded-full blur-3xl pointer-events-none" data-id="element-487"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 text-center" data-id="element-488">
          <img src="https://cdn.magicpatterns.com/uploads/cfHMrrPng3iagpRwV1FuNs/Favicon.jpg" alt="UK Medicolegal Assessments shield logo" className="h-16 w-16 mx-auto mb-8 rounded-xl object-contain" data-id="element-489" />
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6 max-w-4xl mx-auto leading-[1.1]" data-id="element-490">
            Bridging Clinical Complexity and Legal Clarity
          </h1>
          <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed" data-id="element-491">
            UK Medico-Legal Assessments is an independent agency managing a
            panel of GMC-registered specialists. We deliver court-ready medical
            reports for solicitors, insurers, and legal professionals across
            England and Wales.
          </p>
        </div>
      </section>

      {/* 2. MISSION STATEMENT */}
      <section className="py-16 md:py-24 bg-white" data-id="element-492">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8" data-id="element-493">
          <div className="border-l-4 border-teal-500 pl-6 md:pl-10 py-2" data-id="element-494">
            <blockquote className="text-2xl md:text-3xl font-medium text-ink-900 leading-snug" data-id="element-495">
              "We bridge the gap between clinical complexity and legal clarity.
              By managing the administrative and clinical governance, we allow
              experts to focus on the evidence and solicitors to focus on the
              case."
            </blockquote>
          </div>
        </div>
      </section>

      {/* 3. KEY STATS BAR */}
      <section className="bg-[#2D3748] py-16" ref={statsRef} data-id="element-496">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8" data-id="element-497">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 text-center divide-x-0 lg:divide-x divide-slate-600" data-id="element-498">
            <div className="flex flex-col items-center" data-id="element-499">
              <div className="text-4xl md:text-5xl font-bold text-white mb-2 tracking-tight" data-id="element-500">
                {stats15k.toLocaleString()}+
              </div>
              <div className="text-sm font-medium text-slate-300 uppercase tracking-wider" data-id="element-501">
                Reports Delivered
              </div>
            </div>
            <div className="flex flex-col items-center" data-id="element-502">
              <div className="text-4xl md:text-5xl font-bold text-white mb-2 tracking-tight" data-id="element-503">
                {stats1k.toLocaleString()}+
              </div>
              <div className="text-sm font-medium text-slate-300 uppercase tracking-wider" data-id="element-504">
                GMC-Registered Experts
              </div>
            </div>
            <div className="flex flex-col items-center" data-id="element-505">
              <div className="text-4xl md:text-5xl font-bold text-white mb-2 tracking-tight" data-id="element-506">
                {stats98}%
              </div>
              <div className="text-sm font-medium text-slate-300 uppercase tracking-wider" data-id="element-507">
                Report Acceptance Rate
              </div>
            </div>
            <div className="flex flex-col items-center" data-id="element-508">
              <div className="text-4xl md:text-5xl font-bold text-white mb-2 tracking-tight" data-id="element-509">
                1–3
              </div>
              <div className="text-sm font-medium text-slate-300 uppercase tracking-wider" data-id="element-510">
                Weeks Standard Turnaround
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. THE AGENCY MODEL */}
      <section className="py-20 md:py-32 bg-white" data-id="element-511">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8" data-id="element-512">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-513">
            <h2 className="text-3xl md:text-4xl font-bold text-ink-900 tracking-tight mb-6" data-id="element-514">
              Why an Agency, Not an Individual Expert?
            </h2>
            <p className="text-lg text-ink-600 leading-relaxed" data-id="element-515">
              Instructing an individual expert often means dealing with clinical
              delays, administrative bottlenecks, and inconsistent formatting.
              The agency model solves this.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-516">
            {/* Column 1 */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow" data-id="element-517">
              <div className="w-12 h-12 bg-warm-50 rounded-xl flex items-center justify-center mb-6" data-id="element-518">
                <Shield className="h-6 w-6 text-slate-600" data-id="element-519" />
              </div>
              <h3 className="text-xl font-bold text-[#1A2B3C] mb-4" data-id="element-520">
                Clinical Governance
              </h3>
              <p className="text-ink-600 leading-relaxed" data-id="element-521">
                Every report undergoes internal quality audit before delivery.
                Our clinical lead reviews each instruction to ensure the correct
                specialist is assigned.
              </p>
            </div>

            {/* Column 2 */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow" data-id="element-522">
              <div className="w-12 h-12 bg-warm-50 rounded-xl flex items-center justify-center mb-6" data-id="element-523">
                <Users className="h-6 w-6 text-slate-600" data-id="element-524" />
              </div>
              <h3 className="text-xl font-bold text-[#1A2B3C] mb-4" data-id="element-525">
                Breadth of Expertise
              </h3>
              <p className="text-ink-600 leading-relaxed" data-id="element-526">
                Access 1,000+ specialists across every discipline — from
                orthopaedics and psychiatry to ENT and general practice —
                through a single point of contact.
              </p>
            </div>

            {/* Column 3 */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow" data-id="element-527">
              <div className="w-12 h-12 bg-warm-50 rounded-xl flex items-center justify-center mb-6" data-id="element-528">
                <Activity className="h-6 w-6 text-slate-600" data-id="element-529" />
              </div>
              <h3 className="text-xl font-bold text-[#1A2B3C] mb-4" data-id="element-530">
                Operational Efficiency
              </h3>
              <p className="text-ink-600 leading-relaxed" data-id="element-531">
                We handle triage, scheduling, records management, and quality
                assurance. You instruct once; we manage everything.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. GOVERNANCE FRAMEWORK */}
      <section className="py-20 md:py-32 bg-warm-50" data-id="element-532">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8" data-id="element-533">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-534">
            <h2 className="text-3xl md:text-4xl font-bold text-ink-900 tracking-tight mb-6" data-id="element-535">
              Our Clinical Governance Framework
            </h2>
            <p className="text-lg text-ink-600 leading-relaxed" data-id="element-536">
              Rigorous oversight ensures every report is impartial, accurate,
              and court-ready.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto" data-id="element-537">
            <div className="bg-white rounded-2xl p-8 flex items-start gap-5 border border-[#E2E8F0]" data-id="element-538">
              <div className="w-12 h-12 bg-teal-50 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-539">
                <Scale className="h-6 w-6 text-teal-600" data-id="element-540" />
              </div>
              <div data-id="element-541">
                <h3 className="text-lg font-bold text-ink-900 mb-2" data-id="element-542">
                  Independence
                </h3>
                <p className="text-ink-600 leading-relaxed" data-id="element-543">
                  All experts provide impartial, CPR Part 35 compliant evidence.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 flex items-start gap-5 border border-[#E2E8F0]" data-id="element-544">
              <div className="w-12 h-12 bg-teal-50 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-545">
                <Clock className="h-6 w-6 text-teal-600" data-id="element-546" />
              </div>
              <div data-id="element-547">
                <h3 className="text-lg font-bold text-ink-900 mb-2" data-id="element-548">Triage</h3>
                <p className="text-ink-600 leading-relaxed" data-id="element-549">
                  Clinical lead reviews every case within 24 hours.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 flex items-start gap-5 border border-[#E2E8F0]" data-id="element-550">
              <div className="w-12 h-12 bg-teal-50 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-551">
                <Search className="h-6 w-6 text-teal-600" data-id="element-552" />
              </div>
              <div data-id="element-553">
                <h3 className="text-lg font-bold text-ink-900 mb-2" data-id="element-554">
                  Scrutiny
                </h3>
                <p className="text-ink-600 leading-relaxed" data-id="element-555">
                  Reports audited for clarity, accuracy, and legal compliance.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 flex items-start gap-5 border border-[#E2E8F0]" data-id="element-556">
              <div className="w-12 h-12 bg-teal-50 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-557">
                <Lock className="h-6 w-6 text-teal-600" data-id="element-558" />
              </div>
              <div data-id="element-559">
                <h3 className="text-lg font-bold text-ink-900 mb-2" data-id="element-560">
                  Security
                </h3>
                <p className="text-ink-600 leading-relaxed" data-id="element-561">
                  GDPR-compliant, encrypted, UK-hosted data infrastructure.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. COMPLIANCE BADGES */}
      <section className="py-16 bg-white border-b border-warm-200" data-id="element-562">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8" data-id="element-563">
          <div className="flex flex-wrap justify-center gap-4 md:gap-6" data-id="element-564">
            <div className="flex items-center gap-2.5 bg-warm-50 px-4 py-2.5 rounded-full border border-warm-200" data-id="element-565">
              <Shield className="h-4 w-4 text-sage-600" data-id="element-566" />
              <span className="text-sm font-semibold text-ink-700" data-id="element-567">
                ISO 27001 Certified
              </span>
            </div>
            <div className="flex items-center gap-2.5 bg-warm-50 px-4 py-2.5 rounded-full border border-warm-200" data-id="element-568">
              <CheckCircle className="h-4 w-4 text-sage-600" data-id="element-569" />
              <span className="text-sm font-semibold text-ink-700" data-id="element-570">
                ICO Registered
              </span>
            </div>
            <div className="flex items-center gap-2.5 bg-warm-50 px-4 py-2.5 rounded-full border border-warm-200" data-id="element-571">
              <FileText className="h-4 w-4 text-sage-600" data-id="element-572" />
              <span className="text-sm font-semibold text-ink-700" data-id="element-573">
                £10m PI Insurance
              </span>
            </div>
            <div className="flex items-center gap-2.5 bg-warm-50 px-4 py-2.5 rounded-full border border-warm-200" data-id="element-574">
              <Award className="h-4 w-4 text-sage-600" data-id="element-575" />
              <span className="text-sm font-semibold text-ink-700" data-id="element-576">
                Expert Witness Institute
              </span>
            </div>
            <div className="flex items-center gap-2.5 bg-warm-50 px-4 py-2.5 rounded-full border border-warm-200" data-id="element-577">
              <Database className="h-4 w-4 text-teal-600" data-id="element-578" />
              <span className="text-sm font-semibold text-ink-700" data-id="element-579">
                UK Data Residency
              </span>
            </div>
            <div className="flex items-center gap-2.5 bg-warm-50 px-4 py-2.5 rounded-full border border-warm-200" data-id="element-580">
              <Scale className="h-4 w-4 text-sage-600" data-id="element-581" />
              <span className="text-sm font-semibold text-ink-700" data-id="element-582">
                CPR Part 35 Compliant
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* 7. CTA SECTION */}
      <section className="py-20 md:py-24 bg-teal-600 text-center" data-id="element-583">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8" data-id="element-584">
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-6" data-id="element-585">
            Ready to Instruct?
          </h2>
          <p className="text-lg text-teal-50 leading-relaxed mb-10" data-id="element-586">
            Send us your instruction and we will confirm availability, provide a
            fee quote, and assign your expert within 24 hours.
          </p>

          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-6" data-id="element-587">
            <a href="mailto:info@uk-medicolegal-assessment.com?subject=New%20Instruction" className="w-full sm:w-auto inline-flex items-center justify-center bg-white text-teal-700 hover:bg-warm-50 font-bold rounded-xl px-8 py-4 transition-colors shadow-sm text-base" data-id="element-588">
              <Mail className="mr-2.5 h-5 w-5" data-id="element-589" />
              Instruct Us
            </a>
            <a href="/panel-experts" className="w-full sm:w-auto inline-flex items-center justify-center bg-transparent border-2 border-white/30 text-white hover:bg-white/10 font-bold rounded-xl px-8 py-4 transition-colors text-base" data-id="element-590">
              <Users className="mr-2.5 h-5 w-5" data-id="element-591" />
              View Our Experts
            </a>
          </div>

          <p className="text-sm text-teal-100 font-medium" data-id="element-592">
            Mark "Urgent" in the subject for priority response
          </p>
        </div>
      </section>

      <Footer data-id="element-593" />
    </div>;
}